import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaCalendar, FaList, FaFilter } from 'react-icons/fa';
import AddCourtDateModal from '../components/AddCourtDateModal';
import '../pages/DefendantList.css';

const CourtDateList = () => {
  const navigate = useNavigate();
  const [courtDates, setCourtDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchCourtDates();
  }, [pagination.page, statusFilter, dateFrom, dateTo]);

  const fetchCourtDates = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter && { status: statusFilter }),
        ...(dateFrom && dateTo && { dateFrom, dateTo })
      };

      const response = await axios.get('/court-dates', { params });

      if (response.data.success) {
        setCourtDates(response.data.data);
        setPagination({ ...pagination, ...response.data.pagination });
      }
    } catch (error) {
      console.error('Failed to fetch court dates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      scheduled: 'badge-pending',
      completed: 'badge-completed',
      continued: 'badge-pending',
      cancelled: 'badge-forfeited'
    };
    return statusMap[status?.toLowerCase()] || 'badge-pending';
  };

  const getAppearanceStatusBadgeClass = (status) => {
    const statusMap = {
      appeared: 'badge-active',
      'no-show': 'badge-forfeited',
      excused: 'badge-pending'
    };
    return statusMap[status?.toLowerCase()] || 'badge-pending';
  };

  const groupDatesByMonth = (dates) => {
    const grouped = {};
    dates.forEach(date => {
      const monthYear = new Date(date.scheduled_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      });
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(date);
    });
    return grouped;
  };

  const renderListView = () => (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Defendant</th>
            <th>Court</th>
            <th>Hearing Type</th>
            <th>Bond Number</th>
            <th>Status</th>
            <th>Appearance</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {courtDates.length > 0 ? (
            courtDates.map((courtDate) => (
              <tr key={courtDate.id}>
                <td><strong>{formatDate(courtDate.scheduled_date)}</strong></td>
                <td>{formatTime(courtDate.scheduled_time)}</td>
                <td>{courtDate.defendant_name}</td>
                <td>{courtDate.court_name || '-'}</td>
                <td>{courtDate.hearing_type || 'Initial Appearance'}</td>
                <td>{courtDate.bond_number || '-'}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(courtDate.status)}`}>
                    {courtDate.status || 'scheduled'}
                  </span>
                </td>
                <td>
                  {courtDate.appearance_status ? (
                    <span className={`badge ${getAppearanceStatusBadgeClass(courtDate.appearance_status)}`}>
                      {courtDate.appearance_status}
                    </span>
                  ) : '-'}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => navigate(`/court-dates/${courtDate.id}`)}
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center">
                No court dates found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderCalendarView = () => {
    const groupedDates = groupDatesByMonth(courtDates);

    return (
      <div className="calendar-view">
        {Object.keys(groupedDates).length > 0 ? (
          Object.entries(groupedDates).map(([monthYear, dates]) => (
            <div key={monthYear} className="calendar-month-group">
              <h3 className="calendar-month-header">{monthYear}</h3>
              <div className="calendar-events">
                {dates.map(courtDate => (
                  <div
                    key={courtDate.id}
                    className="calendar-event-card"
                    onClick={() => navigate(`/court-dates/${courtDate.id}`)}
                  >
                    <div className="calendar-event-date">
                      <div className="event-day">
                        {new Date(courtDate.scheduled_date).toLocaleDateString('en-US', { day: 'numeric' })}
                      </div>
                      <div className="event-weekday">
                        {new Date(courtDate.scheduled_date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                    </div>
                    <div className="calendar-event-details">
                      <div className="event-time">{formatTime(courtDate.scheduled_time)}</div>
                      <div className="event-defendant">{courtDate.defendant_name}</div>
                      <div className="event-court">{courtDate.court_name || 'Court TBD'}</div>
                      <div className="event-type">{courtDate.hearing_type || 'Initial Appearance'}</div>
                      <div className="event-badges">
                        <span className={`badge ${getStatusBadgeClass(courtDate.status)}`}>
                          {courtDate.status || 'scheduled'}
                        </span>
                        {courtDate.appearance_status && (
                          <span className={`badge ${getAppearanceStatusBadgeClass(courtDate.appearance_status)}`}>
                            {courtDate.appearance_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center" style={{ padding: '40px' }}>
            No court dates found
          </div>
        )}
      </div>
    );
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setPagination({ ...pagination, page: 1 });
  };

  const hasActiveFilters = statusFilter || dateFrom || dateTo;

  const handleSaveCourtDate = (courtDate) => {
    // Refresh the list after saving
    fetchCourtDates();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Court Dates</h1>
          <p className="page-subtitle">
            Manage all court appointments • {pagination.total} total
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div className="view-toggle">
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('list')}
            >
              <FaList /> List
            </button>
            <button
              className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('calendar')}
            >
              <FaCalendar /> Calendar
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Schedule Court Date
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filters-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="continued">Continued</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From Date"
            className="filter-input"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To Date"
            className="filter-input"
          />

          {hasActiveFilters && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleClearFilters}
              title="Clear filters"
            >
              <FaFilter /> Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading court dates...</p>
        </div>
      ) : (
        <>
          {viewMode === 'list' ? renderListView() : renderCalendarView()}

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <AddCourtDateModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveCourtDate}
      />
    </div>
  );
};

export default CourtDateList;
